/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import {
	ChangeDetectionStrategy,
	Component,
	ContentChildren,
	type QueryList,
	computed,
	input,
	signal
} from "@angular/core";
import { SidebarComponent } from "@app/widgets/base/sidebar/sidebar.component";
import { TopNavbarComponent } from "../../top-navbar/top-navbar.component";
import { LayoutColComponent } from "../layout-col/layout-col.component";
import { LayoutRowComponent } from "../layout-row/layout-row.component";
import { LayoutToolbarComponent } from "../layout-toolbar/layout-toolbar.component";

@Component({
	selector: "cb-layout",
	templateUrl: "./layout.component.html",
	styleUrl: "./layout.component.scss",
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayoutComponent {
	/**
	 * If true, the layout will fill the entire viewport
	 */
	public readonly fillViewport = input(false);

	public hasHeader = signal(false);
	public hasFooter = signal(false);

	public hasRows = signal(false);
	public hasCols = signal(false);

	public hasLeftSidebar = signal(false);
	public hasRightSidebar = signal(false);

	public hasTopNavbar = signal(false);

	/**
	 * Checks if the layout has a header or footer
	 */
	@ContentChildren(LayoutToolbarComponent)
	public set toolbars(values: QueryList<LayoutToolbarComponent>) {
		this.hasHeader.set(values.some((toolbar) => toolbar.area() === "header"));
		this.hasFooter.set(values.some((toolbar) => toolbar.area() === "footer"));
	}

	/**
	 * Checks if the layout has a body
	 */
	@ContentChildren(LayoutRowComponent)
	public set rows(values: QueryList<LayoutRowComponent>) {
		this.hasRows.set(values.length > 0);

		if (this.hasRows() && this.hasCols()) {
			throw new Error("A layout cannot have both rows and cols");
		}
	}

	/**
	 * Checks if the layout has a row
	 */
	@ContentChildren(LayoutColComponent)
	public set cols(values: QueryList<LayoutColComponent>) {
		this.hasCols.set(values.length > 0);

		if (this.hasRows() && this.hasCols()) {
			throw new Error("A layout cannot have both rows and cols");
		}
	}

	/**
	 * Checks if the layout has a left or right sidebar
	 */
	@ContentChildren(SidebarComponent)
	public set sidebars(values: QueryList<SidebarComponent>) {
		this.hasLeftSidebar.set(values.some((col) => col.position() === "left"));
		this.hasRightSidebar.set(values.some((col) => col.position() === "right"));
	}

	@ContentChildren(TopNavbarComponent)
	public set topNavbar(values: QueryList<TopNavbarComponent>) {
		this.hasTopNavbar.set(values.length > 0);
	}


	public readonly hasBody = computed(() => {
		return this.hasRows() || this.hasCols();
	});
}
